import * as _ from 'lodash';

/**
 * The coercions and predicates that decide what may reach `POST /v2/sessions/{id}/movementsbulk`
 * (and its `movements` sibling).
 *
 * They live here, not on the grid component that first needed them, because the endpoint has TWO
 * callers — `TableEditRowsComponent` (set-insert) and `AddMovementSessionActionAllComponent` →
 * `AddMovementService.saveMovementBulk` (transfer / unload) — and a belt implemented in one of
 * them is a property of that caller, not of the endpoint. The original defect (a NaN quantity
 * serializing as `"quantity":null` and failing the WHOLE bulk with
 * `error.session.execution_errors`) was fixed on the set-insert path while the transfer/unload
 * path still assembled `quantity` verbatim; "nothing unsendable reaches this endpoint" only holds
 * if both callers go through the same code.
 */

/**
 * A payload value the wire must never see.
 *
 * `_.omitBy(_.isNil)` keeps NaN and Infinity - neither is nil - and `JSON.stringify` turns both
 * into `null`, which is what failed the bulk POST with `error.session.execution_errors`. This is
 * the single sanitiser for BOTH save paths (`setAll` and `saveRow`) and it covers every field
 * rather than quantity alone: `purchasePrice` is restored, copied and serialized by exactly the
 * same route, so a per-field guard in `filterFN` would have to be re-added for each new numeric
 * column.
 */
export const isUnsendable = (value: unknown): boolean =>
  _.isNil(value) || (typeof value === 'number' && !Number.isFinite(value));

/** The one numeric reading every coercion below shares: a number as-is, a non-empty numeric
 *  string via `Number(...)`, anything else NaN. Shared so a future tweak cannot be applied to one
 *  coercion and missed on its siblings — the same one-branch-fixed hazard the `movementFields`
 *  extraction closed for the payload shapes.
 *
 *  `Number(...)` and not the app's lenient `parseNumber`: that parser is preference-aware and
 *  resolves a lone separator by the operator's own grouping glyph, which is right for what a
 *  human TYPES and wrong for a stored or wire value. Under the `us` preset a canonical `'1.234'`
 *  has a three-digit tail and would read as grouping — 1234 — silently rescaling a price by a
 *  thousand. Storage and the wire speak dot-decimal whatever the display preset says. */
export const toNumeric = (value: unknown): number =>
  typeof value === 'number'
    ? value
    : (typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN);

/**
 * A row's quantity as a usable NUMBER - finite AND positive, AFTER coercion - or `undefined`.
 *
 * A bare `Number.isFinite` would be narrower than the `!= undefined` it replaced: it also rejects
 * a numeric STRING, and a quantity can legitimately arrive as `"3"` (a backend payload that
 * serializes it that way, a draft written outside the number directive). Dropping such a row from
 * the bulk is worse than the NaN this guards against - the save still reports success, resets the
 * grid and deletes the draft, so the row is gone with no error anywhere.
 *
 * `> 0` because the component already treats a non-positive quantity as no quantity at all: the
 * inputs declare `[min]="1"` and `validInput` resets anything `<= 0` back to undefined. Leaving 0
 * in was how the per-row save button - which used to inline `!item.quantity`, false for 0 - became
 * clickable for a row whose own input calls the value invalid, and a zero-quantity movement is
 * either a no-op row or the same bulk rejection this guard exists to prevent. A 0 can still reach
 * `copyProducts` without passing through `validInput` (a restored draft, an apply-from-header with
 * an emptied box), so the predicate, not the input, is where it has to be refused.
 */
export const coerceQuantity = (value: unknown): number | undefined => {
  const coerced = toNumeric(value);
  // Integer, not merely finite: the backend field is integral, so a fractional quantity (a draft
  // written by an older build — the live input is `appNumberInput="integer"` and cannot produce
  // one) would fail the WHOLE bulk with `error.session.execution_errors`, the exact
  // one-bad-row-kills-the-batch failure this coercion exists to prevent. Refused, the row simply
  // stays on screen out of the save count with its buttons greyed — same treatment as a negative
  // purchase price — until the operator corrects the cell. `Number.isInteger` already implies
  // finite.
  return Number.isInteger(coerced) && coerced > 0 ? coerced : undefined;
};

/**
 * The predicate form of `coerceQuantity`, for the gates that only need a yes/no.
 *
 * It answers on the COERCED value, so the payload builder has to store the coerced NUMBER rather
 * than the string it accepted: `isUnsendable('3')` is false, so a string that only this predicate
 * ever looked at would go out as `"quantity":"3"` against a numeric backend field. `getModel` is
 * the one place that conversion happens - see the `quantity:` line there.
 */
export const isUsableQuantity = (value: unknown): boolean => coerceQuantity(value) !== undefined;

/**
 * A row's purchase price as a NUMBER, or `undefined` when nothing usable is stored.
 *
 * The sibling of `coerceQuantity`, for the field the `isUnsendable` doc-comment itself names as
 * "restored, copied and serialized by exactly the same route": a draft written outside the number
 * directive (or a backend payload) can carry the price as the string `"2.5"`, and a verbatim copy
 * puts `"purchasePrice":"2.5"` on the wire against a numeric backend field — the same
 * whole-bulk rejection the quantity coercion exists to prevent. A non-numeric string becomes
 * `undefined` (the payload omits the field) rather than going out quoted.
 *
 * No `> 0` clause, unlike quantity: a price of 0 is a legitimate stored value (the input's own
 * validator, `validInputDouble`, only refuses negatives), and today's payload sends it. Negatives
 * are refused for the same reason the input refuses them — no route should put on the wire what
 * every input calls invalid.
 */
export const coercePurchasePrice = (value: unknown): number | undefined => {
  const coerced = toNumeric(value);
  return Number.isFinite(coerced) && coerced >= 0 ? coerced : undefined;
};

/**
 * True exactly when a stored purchase price is a finite NEGATIVE number (same coercion as above) —
 * the one shape the screen renders as a real value while `coercePurchasePrice` silently drops it
 * from the payload. NaN and a non-numeric string render as a BLANK box, so stripping those matches
 * what the operator sees; a negative shows "-2.5" at save time while the movement would book with
 * NO cost recorded, screen and stock disagreeing with no error anywhere. Such a row is refused by
 * `canSaveRow` (and `saveRow`) instead — visibly out of the count, its save buttons greyed — until
 * the cell is corrected. The input's own validator (`validInputDouble`) already refuses negatives,
 * so a value like this can only arrive by the routes the coercion doc names: a draft written by an
 * older build, or a backend payload.
 */
export const isNegativePurchasePrice = (value: unknown): boolean => {
  const coerced = toNumeric(value);
  return Number.isFinite(coerced) && coerced < 0;
};

/**
 * True for a stored quantity the screen renders as a REAL number while `coerceQuantity` refuses it
 * — zero, negative, or fractional (a draft written by an older build; the live input is
 * integer-mode and `[min]="1"`). The counterpart of `isNegativePurchasePrice` for the quantity
 * cell, and the discriminator the per-row warning needs: NaN and a garbage string render as a
 * BLANK box, so the operator can see for themselves why the row is not counted, but a visible `0`
 * or `2,5` in a fully-filled row that silently never saves is a dead end with no signal anywhere —
 * the row stays out of the "save N" count, both buttons grey, and every autosave re-persists it
 * into the draft unchanged. The warning icon (`rowValueWarningKey`) names the cell instead.
 */
export const isBlockedQuantity = (value: unknown): boolean =>
  Number.isFinite(toNumeric(value)) && coerceQuantity(value) === undefined;
